/**
 * AuthContext - Manages authentication state across the entire application.
 * Provides user session data, login/logout functions, role-checking utilities,
 * and impersonation ("Sudo Sign In") support for Super Admins.
 *
 * Supports three auth providers:
 *	 1. Email/password - credentials are posted to the API, tokens are returned
 *	 2. Office 365 (Azure AD) - full-page redirect to backend OAuth endpoint
 *	 3. Google OAuth - full-page redirect to backend OAuth endpoint
 *
 * Multi-Role System:
 *	 Users can now have multiple roles via the user_roles junction table.
 *	 The `roles` field on the User object is an array of role slugs (e.g.,
 *	 ['admin', 'company_admin']). The `role` field is kept for backward
 *	 compatibility and contains the primary/highest-privilege role.
 *
 * Impersonation:
 *	 Super Admins can "sign in as" any user to see the platform from their
 *	 perspective. During impersonation, the original admin's token is stored
 *	 in localStorage so the session can be reverted.
 *
 * User session is persisted via Sanctum-style tokens stored in localStorage
 * under the keys "auth_token" and "refresh_token". On app mount, the provider
 * checks for an existing token and attempts to restore the session by calling
 * authService.getCurrentUser().
 *
 * Usage: Wrap your app in <AuthProvider>, then use the useAuth() hook in
 * child components to access auth state and methods.
 */
import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { User } from '../services/authService';
import { authService } from '../services/authService';
import type { UserRole } from '../utils/constants';
import { resolveStorageUrl } from '../utils/resolveStorageUrl';
// Import the offline storage service so we can flush pending data before session-expiry logout
import { getPendingCount } from '../services/offlineStorage';
// Persisted Filter Control preferences are scoped to a signed-in session — wipe
// them on sign-out so the next session starts from the app defaults.
import { clearPersistedFilters } from '../hooks/usePersistedFilterState';
import { clearQueryCache } from '../lib/queryClient';

/**
 * Maps the snake_case API user response to the camelCase User interface.
 * The Laravel backend returns snake_case field names (first_name, company_id, etc.)
 * but the frontend User type uses camelCase (firstName, companyId, etc.).
 *
 * Handles both the new multi-role system (role_names array from the backend)
 * and the legacy single-role column for backward compatibility.
 */
const mapApiUserToUser = (apiUser: Record<string, any>): User => ({
	id: String(apiUser.id),
	email: apiUser.email,
	firstName: apiUser.first_name,
	lastName: apiUser.last_name,
	// Map role_names from the backend (array of role slugs from junction table)
	// Falls back to wrapping the legacy single 'role' column in an array
	roles: Array.isArray(apiUser.role_names) ? apiUser.role_names : [apiUser.role],
	// Primary role: first element of role_names array, or legacy role column
	role: Array.isArray(apiUser.role_names) ? apiUser.role_names[0] : apiUser.role,
	phone: apiUser.phone || null,
	companyId: apiUser.company_id ? String(apiUser.company_id) : '',
	// Map company_ids from the API response (array of company IDs from the
	// company_users junction table) to the frontend's companyIds array.
	// Falls back to wrapping the primary company_id in an array if not present.
	companyIds: Array.isArray(apiUser.company_ids)
		? apiUser.company_ids.map((id: any) => String(id))
		: (apiUser.company_id ? [String(apiUser.company_id)] : []),
	canViewFinancials: Boolean(apiUser.can_view_financials),
	canReassign: Boolean(apiUser.can_reassign),
	profilePhotoUrl: resolveStorageUrl(apiUser.profile_photo_url),
	profilePhotoThumbUrl: resolveStorageUrl(apiUser.profile_photo_thumb_url),
	authProvider: apiUser.auth_provider || 'email',
	// Force password change flag — set when user is created by admin or after admin password reset
	mustChangePassword: Boolean(apiUser.must_change_password),
	// IANA timezone identifier — defaults to Eastern Time if not set on the backend
	timezone: apiUser.timezone || 'America/New_York',
});

/**
 * Shape of the original user info stored during impersonation.
 * Contains just enough data to display the impersonation banner
 * and revert the session.
 */
interface OriginalUserInfo {
	/** Original admin user's ID */
	id: number;
	/** Original admin user's display name */
	name: string;
}

/**
 * AuthContextValue - Shape of the value exposed by AuthContext.
 *
 * @property user				- The currently authenticated user object, or null if logged out.
 * @property isAuthenticated	- Convenience boolean derived from `user !== null`.
 * @property isLoading			- True while the initial session-restore request is in-flight.
 * @property login				- Authenticates with email/password credentials.
 * @property loginWithO365		- Redirects the browser to the Office 365 OAuth flow.
 * @property loginWithGoogle	- Redirects the browser to the Google OAuth flow.
 * @property logout				- Clears tokens, calls the logout API, and resets user state.
 * @property hasRole			- Returns true if the current user has the specified role.
 * @property hasAnyRole			- Returns true if the current user has any of the specified roles.
 * @property updateUser			- Merges partial updates into the current user state.
 * @property isImpersonating	- True if the current session is an impersonated user.
 * @property originalUser		- The original admin's info during impersonation, or null.
 * @property impersonate		- Start impersonating a target user (super_admin only).
 * @property stopImpersonation	- Revert to the original admin session.
 * @property passwordChangeDue	- True when this session must set a new password first.
 */
interface AuthContextValue {
	user: User | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	/** Authenticate with email/password; optional rememberMe controls token expiry and email persistence */
	login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
	loginWithO365: () => Promise<void>;
	loginWithGoogle: () => Promise<void>;
	logout: () => Promise<void>;
	hasRole: (role: UserRole) => boolean;
	hasAnyRole: (roles: UserRole[]) => boolean;
	/** Update the user object in context (e.g., after profile edit) so nav/UI reflects changes immediately */
	updateUser: (updates: Partial<User>) => void;
	/** Whether the current session is an impersonated user */
	isImpersonating: boolean;
	/** The original admin's info during impersonation, or null when not impersonating */
	originalUser: OriginalUserInfo | null;
	/** Start impersonating a target user — only callable by super_admin */
	impersonate: (userId: string) => Promise<void>;
	/** Stop impersonating and revert to the original admin session */
	stopImpersonation: () => Promise<void>;
	/**
	 * True when this session has to set a new password before anything else
	 * (a first sign-in, or after an admin reset). Never while impersonating: the
	 * password is the other person's to choose, and they are still asked on
	 * their own first sign-in. Sending the admin to the change form also hid the
	 * banner that ends the impersonation.
	 */
	passwordChangeDue: boolean;
	/** True when the session token will expire within the next 60 seconds */
	sessionExpiringSoon: boolean;
}

// Create the context with undefined default so we can detect missing providers
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const location = useLocation();

	// --- Auth state ---
	const [user, setUser] = useState<User | null>(null);
	// Starts as true because we need to check for an existing token on mount
	const [isLoading, setIsLoading] = useState(true);

	// --- Session expiry state ---
	// True when the token will expire within the next 60 seconds (triggers warning UI)
	const [sessionExpiringSoon, setSessionExpiringSoon] = useState(false);
	// Ref to hold the interval ID so we can clear it on unmount or logout
	const expiryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	// --- Impersonation state ---
	// Track whether the current session is an impersonation and store original admin info
	const [isImpersonating, setIsImpersonating] = useState(false);
	const [originalUser, setOriginalUser] = useState<OriginalUserInfo | null>(null);

	// --- Session restoration ---
	// If an auth token exists in localStorage, attempt to fetch the current
	// user profile from the API. Also restore impersonation state if present.
	// Skip restore on OAuth callback routes — OAuthCallbackPage stores the
	// token and navigates client-side to "/"; restore runs again on that route.
	useEffect(() => {
		const token = localStorage.getItem('auth_token');

		// Restore impersonation state from localStorage if present
		const storedOriginalUser = localStorage.getItem('original_user');
		const storedOriginalToken = localStorage.getItem('original_auth_token');
		if (storedOriginalUser && storedOriginalToken) {
			try {
				setOriginalUser(JSON.parse(storedOriginalUser));
				setIsImpersonating(true);
			} catch {
				// Invalid JSON — clear the stored impersonation state
				localStorage.removeItem('original_user');
				localStorage.removeItem('original_auth_token');
			}
		}

		// On the OAuth callback route, OAuthCallbackPage stores the token and
		// then navigates client-side to "/". We must NOT clear isLoading here:
		// doing so would let ProtectedRoute on "/" render with isLoading=false
		// and user=null (the restore below hasn't run yet) and bounce the user
		// to /login before the session is restored. Staying in the loading state
		// bridges the handoff until this effect re-runs on "/" and the restore
		// resolves and sets the user.
		if (location.pathname.startsWith('/auth/callback')) {
			setIsLoading(true);
			return;
		}

		// Session already restored (or set by email login) — no refetch on navigation
		if (user !== null) {
			setIsLoading(false);
			return;
		}

		if (token) {
			setIsLoading(true);
			authService
				.getCurrentUser()
				.then((res) => {
					// /auth/me returns { user: {...}, permissions: {...} }
					// Extract the user object from the response wrapper
					const userData = (res.data as any).user || res.data;
					setUser(mapApiUserToUser(userData));

					// Restore the session expiry check if a token_expires_at was
					// stored during the original login. This handles page refreshes
					// so the expiry warning and auto-logout continue to work.
					const storedExpiresAt = localStorage.getItem('token_expires_at');
					if (storedExpiresAt) {
						const remainingMs = new Date(storedExpiresAt).getTime() - Date.now();
						if (remainingMs <= 0) {
							// Token already expired while the page was closed — log out immediately
							handleSessionExpiry();
						} else {
							// Token still valid — resume the expiry check interval
							startExpiryCheck(storedExpiresAt);
						}
					}
				})
				.catch(() => {
					// Token is invalid or expired - clean up stored tokens
					localStorage.removeItem('auth_token');
					localStorage.removeItem('refresh_token');
					localStorage.removeItem('token_expires_at');
					// Also clear impersonation state if token is invalid
					localStorage.removeItem('original_auth_token');
					localStorage.removeItem('original_user');
					setIsImpersonating(false);
					setOriginalUser(null);
				})
				.finally(() => setIsLoading(false));
		} else {
			// No token found - skip the API call and mark loading as complete
			setIsLoading(false);
		}

		// Cleanup: clear the expiry interval when the AuthProvider unmounts
		// to prevent memory leaks and stale timer callbacks
		return () => {
			if (expiryIntervalRef.current) {
				clearInterval(expiryIntervalRef.current);
				expiryIntervalRef.current = null;
			}
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, user]);

	/**
	 * handleSessionExpiry - Gracefully ends an expired session.
	 *
	 * Before clearing auth state, checks if there are any pending offline
	 * items in IndexedDB. The data stays in IndexedDB (it persists across
	 * sessions), so it will be synced the next time the user logs in and
	 * goes online. After the check, clears all auth tokens and resets the
	 * user state which triggers the router to redirect to /login.
	 */
	const handleSessionExpiry = useCallback(async () => {
		try {
			// Check for pending offline data — it remains in IndexedDB safely
			// and will be synced on the user's next authenticated session
			const pendingCount = await getPendingCount();
			if (pendingCount > 0) {
				// Data is already persisted in IndexedDB; no additional save needed.
				// Log for debugging purposes so developers can verify data was preserved.
				console.info(`[AuthContext] Session expired with ${pendingCount} pending offline item(s). Data preserved in IndexedDB.`);
			}
		} catch {
			// IndexedDB access can fail in rare cases (e.g., private browsing quota);
			// proceed with logout regardless to maintain security
			console.warn('[AuthContext] Could not check offline data before session expiry.');
		}

		// Clear the expiry check interval since we're logging out
		if (expiryIntervalRef.current) {
			clearInterval(expiryIntervalRef.current);
			expiryIntervalRef.current = null;
		}

		// Remove all auth-related localStorage items
		localStorage.removeItem('auth_token');
		localStorage.removeItem('refresh_token');
		localStorage.removeItem('token_expires_at');
		// Clear impersonation state if present
		localStorage.removeItem('original_auth_token');
		localStorage.removeItem('original_user');
		// Drop the user's saved Filter Control selections so the next session
		// falls back to the app defaults rather than inheriting stale filters.
		clearPersistedFilters();
		// Wipe the query cache (in-memory + persisted copy) so the next user
		// on this device never sees this session's cached reads.
		await clearQueryCache();

		// Reset all auth context state — this triggers ProtectedRoute to redirect to /login
		setUser(null);
		setSessionExpiringSoon(false);
		setIsImpersonating(false);
		setOriginalUser(null);
	}, []);

	/**
	 * startExpiryCheck - Sets up a 30-second interval that monitors the
	 * token's remaining lifetime.
	 *
	 * When the token has less than 60 seconds remaining, sets the
	 * `sessionExpiringSoon` flag to true so the UI can warn the user.
	 * When the token has fully expired (0 seconds remaining), calls
	 * handleSessionExpiry() to gracefully end the session.
	 *
	 * Only starts if `expiresAt` is a valid ISO timestamp (non-null).
	 * Tokens with no expiry (remember_me = true) skip this entirely.
	 *
	 * @param expiresAt - ISO 8601 expiry timestamp from the login response
	 */
	const startExpiryCheck = useCallback((expiresAt: string) => {
		// Clear any existing interval from a previous session
		if (expiryIntervalRef.current) {
			clearInterval(expiryIntervalRef.current);
		}

		// Check every 30 seconds whether the token is about to expire
		expiryIntervalRef.current = setInterval(() => {
			const now = Date.now();
			const expiry = new Date(expiresAt).getTime();
			// Calculate how many milliseconds remain until the token expires
			const remainingMs = expiry - now;

			if (remainingMs <= 0) {
				// Token has expired — trigger graceful session teardown
				handleSessionExpiry();
			} else if (remainingMs <= 60_000) {
				// Less than 1 minute remaining — show the warning banner/toast
				setSessionExpiringSoon(true);
			}
		}, 30_000);
	}, [handleSessionExpiry]);

	// --- Email/password login ---
	// Sends credentials to the API, stores the returned access and refresh
	// tokens in localStorage, and sets the user in state. The optional
	// rememberMe parameter controls token expiry and email persistence.
	const login = useCallback(async (email: string, password: string, rememberMe: boolean = true) => {
		// Pass remember_me to the backend so it can set token expiry accordingly
		const res = await authService.login({ email, password, remember_me: rememberMe });

		// Store the Sanctum token for subsequent API requests
		localStorage.setItem('auth_token', res.data.token);

		// Store or clear the token expiry timestamp based on the backend response.
		// When expires_at is null, the token has no expiry (remember_me = true).
		if (res.data.expires_at) {
			localStorage.setItem('token_expires_at', res.data.expires_at);
			// Start the interval that monitors remaining token lifetime
			startExpiryCheck(res.data.expires_at);
		} else {
			// No expiry — remove any stale expiry from a previous session
			localStorage.removeItem('token_expires_at');
		}

		// Map snake_case API response to camelCase User interface
		setUser(mapApiUserToUser(res.data.user));
	}, [startExpiryCheck]);

	// --- Office 365 OAuth login ---
	// Performs a full-page redirect to the backend's O365 auth endpoint.
	// The backend handles the OAuth handshake and redirects back with tokens.
	const loginWithO365 = useCallback(async () => {
		window.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/auth/o365`;
	}, []);

	// --- Google OAuth login ---
	// Same pattern as O365: full-page redirect to the backend Google auth endpoint.
	const loginWithGoogle = useCallback(async () => {
		window.location.href = `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/auth/google`;
	}, []);

	// --- Logout ---
	// Calls the API logout endpoint (to invalidate server-side tokens),
	// then always clears local tokens and resets user state regardless
	// of whether the API call succeeds (e.g., if the user is already offline).
	const logout = useCallback(async () => {
		try {
			await authService.logout();
		} finally {
			// Clear the session expiry interval to prevent stale callbacks
			if (expiryIntervalRef.current) {
				clearInterval(expiryIntervalRef.current);
				expiryIntervalRef.current = null;
			}
			localStorage.removeItem('auth_token');
			localStorage.removeItem('refresh_token');
			// Clear session expiry timestamp
			localStorage.removeItem('token_expires_at');
			// Clear impersonation state on logout
			localStorage.removeItem('original_auth_token');
			localStorage.removeItem('original_user');
			// Drop the user's saved Filter Control selections so the next session
			// falls back to the app defaults rather than inheriting stale filters.
			clearPersistedFilters();
			// Wipe the query cache (in-memory + persisted copy) so the next user
			// on this device never sees this session's cached reads.
			await clearQueryCache();
			setUser(null);
			setSessionExpiringSoon(false);
			setIsImpersonating(false);
			setOriginalUser(null);
		}
	}, []);

	// --- Role-checking utilities ---
	// Updated to check against the `roles` array (multi-role system).
	// Falls back to checking the single `role` field for backward compat.
	const hasRole = useCallback(
		(role: UserRole) => {
			if (!user) return false;
			// Check the roles array first (new multi-role system)
			if (user.roles && user.roles.length > 0) {
				return user.roles.includes(role);
			}
			// Fallback to legacy single role field
			return user.role === role;
		},
		[user]
	);

	const hasAnyRole = useCallback(
		(roles: UserRole[]) => {
			if (!user) return false;
			// Check the roles array first (new multi-role system)
			if (user.roles && user.roles.length > 0) {
				return roles.some((role) => user.roles.includes(role));
			}
			// Fallback to legacy single role field
			return roles.includes(user.role);
		},
		[user]
	);

	// --- Update user in context ---
	// Called after profile edits so the nav/UI reflects changes (e.g., name) immediately
	const updateUser = useCallback((updates: Partial<User>) => {
		setUser((prev) => prev ? { ...prev, ...updates } : prev);
	}, []);

	// --- Impersonation: Start ---
	// Calls the backend impersonate endpoint, stores the original admin's
	// token and info in localStorage, and switches to the target user session.
	const impersonate = useCallback(async (userId: string) => {
		// Save the current (admin) token before switching
		const currentToken = localStorage.getItem('auth_token');
		if (!currentToken) return;

		// Call the impersonation endpoint on the backend
		const res = await authService.impersonate(userId);

		// Store the original admin's token so we can revert later
		localStorage.setItem('original_auth_token', currentToken);

		// Store the original admin's identity for the impersonation banner
		const origUser = res.data.original_user;
		localStorage.setItem('original_user', JSON.stringify(origUser));

		// Switch to the impersonated user's token
		localStorage.setItem('auth_token', res.data.token);

		// Cached reads belong to whoever fetched them, and the query keys don't
		// say who that was, so drop them like a sign-out does. Otherwise the
		// admin sees their own lists as this user, and after stopping sees
		// this user's (a super admin was shown only Abbey's companies).
		clearPersistedFilters();
		await clearQueryCache();

		// Update the auth context state with the impersonated user
		setUser(mapApiUserToUser(res.data.user));
		setIsImpersonating(true);
		setOriginalUser(origUser);
	}, []);

	// --- Impersonation: Stop ---
	// Calls the backend stop-impersonation endpoint with the original token,
	// restores the original admin session, and clears impersonation state.
	const stopImpersonation = useCallback(async () => {
		// Retrieve the original admin's token from localStorage
		const originalToken = localStorage.getItem('original_auth_token');
		if (!originalToken) return;

		// Call the backend to revoke the impersonation token and get original user data
		const res = await authService.stopImpersonation(originalToken);

		// Restore the original admin's token as the active auth token
		localStorage.setItem('auth_token', res.data.token);

		// Clear impersonation-related localStorage items
		localStorage.removeItem('original_auth_token');
		localStorage.removeItem('original_user');

		// Drop everything cached as the impersonated user (see impersonate).
		clearPersistedFilters();
		await clearQueryCache();

		// Restore the original admin user in the auth context
		setUser(mapApiUserToUser(res.data.user));
		setIsImpersonating(false);
		setOriginalUser(null);
	}, []);

	const passwordChangeDue = Boolean(user?.mustChangePassword) && !isImpersonating;

	return (
		<AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, login, loginWithO365, loginWithGoogle, logout, hasRole, hasAnyRole, updateUser, isImpersonating, originalUser, impersonate, stopImpersonation, passwordChangeDue, sessionExpiringSoon }}>
			{children}
		</AuthContext.Provider>
	);
};
